<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
  layout: WebLayout,
}
</script>

<script setup>
import { computed, onMounted } from 'vue'
import SEOHead from '@/components/SEOHead.vue'
import { useScrollReveal } from '@/composables/useScrollReveal.js'
import { useWebTranslations } from '@/composables/useWebTranslations.js'
import AllFeatures from '@/Pages/Web/components/AllFeatures.vue'

const { get, locale, webRoute, alternateLocales } = useWebTranslations()
const { refresh } = useScrollReveal()

onMounted(() => {
  refresh()
})

const canonicalPath = computed(() => webRoute('web.features'))
const pageAlternateLocales = computed(() => alternateLocales('web.features'))
</script>

<template>
  <SEOHead
    :title="get('static.features.seo_title')"
    :description="get('static.features.seo_description')"
    :canonical="canonicalPath"
    :locale="locale"
    :alternate-locales="pageAlternateLocales"
    type="website"
  />

  <!-- COVER -->
  <section class="relative overflow-hidden isolate">
    <div aria-hidden="true" class="absolute inset-0 pointer-events-none">
      <img
        src="/images/features/cover.webp"
        alt=""
        width="1920"
        height="1080"
        loading="lazy"
        decoding="async"
        class="w-full h-full object-cover"
      />
      <div class="absolute inset-0" style="background: linear-gradient(180deg, rgba(242,240,239,0.45) 0%, rgba(242,240,239,0.72) 55%, rgba(242,240,239,0.95) 100%);"></div>
      <div class="absolute inset-0 gw-pattern opacity-25"></div>
      <div class="grain absolute inset-0"></div>
    </div>
    <div aria-hidden="true" class="absolute inset-0 pointer-events-none">
      <div class="gw-halo-warm" style="top:-6rem; left:-8rem; width:32rem; height:32rem; opacity:0.45;"></div>
      <div class="gw-halo-magenta" style="bottom:-10rem; right:-8rem; width:28rem; height:28rem; opacity:0.4;"></div>
    </div>

    <div class="relative max-w-[1280px] mx-auto px-6 lg:px-12 pt-28 pb-16 lg:pt-44 lg:pb-28">
      <div class="reveal grid grid-cols-12 gap-6 items-end">
        <div class="col-span-12 lg:col-span-9">
          <span class="gw-eyebrow">{{ get('static.features.cover.eyebrow') }}</span>
          <h1 class="gw-display mt-5" style="font-size: clamp(2.5rem, 6vw, 5.25rem); line-height: 0.95; letter-spacing: -0.028em;">
            <em>{{ get('static.features.cover.heading_emphasis') }}</em>{{ get('static.features.cover.heading_rest') }}<br>
            {{ get('static.features.cover.heading_line_2') }}
          </h1>
        </div>
        <div class="col-span-12 lg:col-span-3 lg:pb-3">
          <p class="gw-mono text-[10.5px] uppercase tracking-[0.22em] text-[rgba(51,51,51,0.5)] font-semibold mb-3">
            {{ get('static.features.cover.editorial_label') }}
          </p>
          <p class="text-[0.95rem] text-[color:var(--gw-ink)] leading-[1.55] max-w-[28ch]">
            {{ get('static.features.cover.editorial_prefix') }}
            <em class="italic font-extrabold not-italic-fallback" style="background: var(--gw-gradient); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;">{{ get('static.features.cover.editorial_emphasis') }}</em>.
            {{ get('static.features.cover.editorial_suffix') }}
          </p>
        </div>
      </div>

      <div class="reveal reveal-delay-2 mt-14 lg:mt-20">
        <p class="gw-display max-w-[36ch]" style="font-size: clamp(1.4rem, 2.6vw, 2.4rem); font-weight: 300; line-height: 1.18; color: rgba(51,51,51,0.7); letter-spacing: -0.018em;">
          {{ get('static.features.cover.subdeck_line_1') }}<br>
          <em>{{ get('static.features.cover.subdeck_emphasis') }}</em>{{ get('static.features.cover.subdeck_line_2_suffix') }}
        </p>
      </div>
    </div>
  </section>

  <AllFeatures />
</template>

<style scoped>
.grain {
  background-image: url("/images/icons/grain.svg");
  background-size: 240px 240px;
  opacity: 0.06;
  mix-blend-mode: multiply;
}

.not-italic-fallback { font-style: italic; }
</style>
